package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.dashboard.components.motion.ViewPhase
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.shared.dto.common.PageInfoDto
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import com.kttipay.mvi.api.MVIAction
import com.kttipay.mvi.api.MVIIntent
import com.kttipay.mvi.api.MVIState

object ReleasesContract {
    sealed interface Status {
        data object Idle : Status

        data object Loading : Status

        data object LoadingMore : Status

        data object Loaded : Status

        data object Refreshing : Status

        data class Error(val error: ApiError) : Status
    }

    data class State(
        val workspaceId: String? = null,
        val isAdmin: Boolean = false,
        val status: Status = Status.Idle,
        val items: List<ShipEventDto> = emptyList(),
        val pageInfo: PageInfoDto? = null,
        val repo: String? = null,
        val knownRepos: List<String> = emptyList(),
        val settings: ReleaseSettingsDto? = null,
        val github: GithubReach = GithubReach.Unknown,
        val hasEntered: Boolean = false,
        val loadMoreError: ApiError? = null,
    ) : MVIState {
        val showsRows: Boolean
            get() = status == Status.Loaded || status == Status.LoadingMore || status == Status.Refreshing
        val isEmpty: Boolean get() = (status == Status.Loaded || status == Status.Refreshing) && items.isEmpty()
        val phase: ViewPhase
            get() = when (status) {
                Status.Idle, Status.Loading -> ViewPhase.Loading
                is Status.Error -> ViewPhase.Error
                Status.Loaded, Status.LoadingMore, Status.Refreshing -> if (isEmpty) ViewPhase.Empty else ViewPhase.Ready
            }
    }

    sealed interface Intent : MVIIntent {
        data class Load(val workspaceId: String, val isAdmin: Boolean, val repo: String? = null) : Intent

        data object LoadMore : Intent

        data class SetRepo(val repo: String?) : Intent

        data object GoToSettings : Intent

        data object GoToIntegrations : Intent
    }

    sealed interface Action : MVIAction {
        data class Navigate(val path: String) : Action
    }
}

fun ReleasesContract.State.withFirstPage(page: PagedResponse<ShipEventDto>): ReleasesContract.State =
    copy(
        items = page.items,
        pageInfo = page.pageInfo,
        knownRepos = mergedRepos(page.items),
        status = ReleasesContract.Status.Loaded,
        loadMoreError = null,
    )

fun ReleasesContract.State.withNextPage(page: PagedResponse<ShipEventDto>): ReleasesContract.State =
    copy(
        items = items + page.items,
        pageInfo = page.pageInfo,
        knownRepos = mergedRepos(page.items),
        status = ReleasesContract.Status.Loaded,
        loadMoreError = null,
    )

fun ReleasesContract.State.withLoadMoreFailure(error: ApiError): ReleasesContract.State =
    copy(status = ReleasesContract.Status.Loaded, loadMoreError = error)

private fun ReleasesContract.State.mergedRepos(incoming: List<ShipEventDto>): List<String> =
    (knownRepos + incoming.map { it.repo }).distinct().sorted()
